import React, { FC } from 'react';
import Editor from '@monaco-editor/react';
import { ActivityLogsModel } from '../../types/generated/activityLogsModel';
import {
    formatRequestBodyForDisplay,
    getActionTypeLabel,
    isFormActivityEntry,
} from './activityLogUtils';
import ActivityLogDiffView from './ActivityLogDiffView';
import { useTranslation } from 'react-i18next';

export type SidePanelMode = 'empty' | 'pretty' | 'diff' | 'metadata';

interface ActivityLogSidePanelProps {
    mode: SidePanelMode;
    selectedEntry: ActivityLogsModel | null;
    diffOriginal: string;
    diffModified: string;
    isLoading: boolean;
}

const ActivityLogSidePanel: FC<ActivityLogSidePanelProps> = ({
    mode,
    selectedEntry,
    diffOriginal,
    diffModified,
    isLoading,
}) => {
    const { t } = useTranslation(['form-content.logs']);

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center text-studio">
                {t('form-content.logs:activityLog.loading')}
            </div>
        );
    }

    if (mode === 'empty') {
        return (
            <div className="flex h-full items-center justify-center text-studio p-4 text-center">
                {t('form-content.logs:activityLog.selectRowHint')}
            </div>
        );
    }

    if (mode === 'diff') {
        return (
            <div className="h-full min-h-[24rem]">
                <ActivityLogDiffView
                    original={diffOriginal}
                    modified={diffModified}
                />
            </div>
        );
    }

    if (mode === 'metadata' && selectedEntry) {
        return (
            <div className="p-4 space-y-3 text-sm">
                <div>
                    <span className="font-semibold">
                        {t('form-content.logs:activityLog.timestamp')}:{' '}
                    </span>
                    {new Date(selectedEntry.timestamp).toISOString()}
                </div>
                <div>
                    <span className="font-semibold">
                        {t('form-content.logs:activityLog.user')}:{' '}
                    </span>
                    {selectedEntry.userId}
                </div>
                <div>
                    <span className="font-semibold">
                        {t('form-content.logs:activityLog.action')}:{' '}
                    </span>
                    {getActionTypeLabel(selectedEntry.action, t)}
                </div>
                <div>
                    <span className="font-semibold">
                        {t('form-content.logs:activityLog.objectId')}:{' '}
                    </span>
                    {selectedEntry.affectedObjectId}
                </div>
            </div>
        );
    }

    if (mode === 'pretty' && selectedEntry && isFormActivityEntry(selectedEntry)) {
        const body = formatRequestBodyForDisplay(selectedEntry.data);
        return (
            <div className="h-full min-h-[24rem]">
                <Editor
                    height="100%"
                    language="json"
                    value={
                        body ||
                        t('form-content.logs:activityLog.noPayload')
                    }
                    options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                    }}
                />
            </div>
        );
    }

    return null;
};

export default ActivityLogSidePanel;
